import React, { lazy, Suspense } from 'react';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import EzraLayout from '../components/layout/EzraLayout';
import ProtectedRoute from '../components/auth/ProtectedRoute';
import LoadingSpinner from '../components/common/LoadingSpinner';
import useAuthStore from '../stores/authStore';
import NotFound from '../components/common/NotFound';
// import { Permission } from '../types/auth';

// Import direto (versões funcionando)
import ProductsImproved from '../pages/ProductsImproved';
import SyncDashboard from '../components/sync/SyncDashboard';

// Lazy loading das páginas
const Dashboard = lazy(() => import('../pages/Dashboard'));
const SyncDashboardPage = lazy(() => import('../pages/sync/Dashboard'));
const DiscoveryPage = lazy(() => import('../pages/sync/DiscoveryPage'));
const JobsPage = lazy(() => import('../pages/sync/JobsPage'));
const ImportProductsUniversal = lazy(() => import('../pages/sync/ImportProductsUniversal'));
const ImportCategories = lazy(() => import('../pages/sync/ImportCategories'));
const ImportAttributes = lazy(() => import('../pages/sync/ImportAttributes'));
const ImportCustomers = lazy(() => import('../pages/sync/ImportCustomers'));
const ImportBrands = lazy(() => import('../pages/sync/ImportBrands'));
const ExportProducts = lazy(() => import('../pages/sync/ExportProducts'));
const ExportCustomers = lazy(() => import('../pages/sync/ExportCustomers'));
const ExportCategories = lazy(() => import('../pages/sync/ExportCategories'));
const ExportAttributes = lazy(() => import('../pages/sync/ExportAttributes'));
const ProductDetail = lazy(() => import('../pages/ProductDetail'));
const ProductCreate = lazy(() => import('../pages/ProductCreate'));
const ProductEdit = lazy(() => import('../pages/ProductEdit'));
const Orders = lazy(() => import('../pages/Orders'));
const OrderDetail = lazy(() => import('../pages/OrderDetail'));
const Sync = lazy(() => import('../pages/Sync'));
const Settings = lazy(() => import('../pages/Settings'));
const Profile = lazy(() => import('../pages/Profile'));
const Login = lazy(() => import('../pages/Login'));
const AccessDenied = lazy(() => import('../pages/AccessDenied'));
const ComponentsDemo = lazy(() => import('../components/ComponentsDemo'));
const Platforms = lazy(() => import('../pages/configuration/Platforms'));
const PlatformApiConfig = lazy(() => import('../pages/configuration/PlatformApiConfig'));
const LinxDePara = lazy(() => import('../pages/configuration/LinxDePara'));
const ProductHistory = lazy(() => import('../pages/sync/ProductHistory'));
const TinyApiConfig = lazy(() => import('../pages/configuration/TinyApiConfig'));
const BlingApiConfig = lazy(() => import('../pages/configuration/BlingApiConfig'));

// Mini-PIM pages
const Brands = lazy(() => import('../pages/Brands'));
const Companies = lazy(() => import('../pages/Companies'));
const Tasks = lazy(() => import('../pages/Tasks'));
const Users = lazy(() => import('../pages/Users'));

// Documentacao (paginas publicas)
const BlingIntegration = lazy(() => import('../pages/docs/BlingIntegration'));

// Componente de loading para lazy loading
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <LoadingSpinner size="large" />
  </div>
);

// Layout protegido com autenticação e Suspense
const ProtectedLayout = () => {
  console.log('🔍 ProtectedLayout rendering...');
  return (
    <ProtectedRoute requireAuth>
      <EzraLayout />
    </ProtectedRoute>
  );
};

// Definição das rotas
export // Empresas: staff, superuser ou operador (can_create_companies). Owner/admin de
// loja nao lista nem cria clientes; o backend tambem barra (403 no create).
const CompaniesRoute: React.FC = () => {
  const { user } = useAuthStore();
  if (!(user?.is_staff || user?.is_superuser || user?.can_create_companies)) {
    return <Navigate to="/dashboard" replace />;
  }
  return <Companies />;
};

const router = createBrowserRouter([
  // Rotas públicas
  {
    path: '/login',
    element: (
      <Suspense fallback={<PageLoader />}>
        <Login />
      </Suspense>
    ),
  },
  {
    path: '/access-denied',
    element: (
      <Suspense fallback={<PageLoader />}>
        <AccessDenied />
      </Suspense>
    ),
  },

  // Documentacao publica
  {
    path: '/docs/bling',
    element: (
      <Suspense fallback={<PageLoader />}>
        <BlingIntegration />
      </Suspense>
    ),
  },

  // Rota de teste (temporária)
  {
    path: '/products-test',
    element: (
      <Suspense fallback={<PageLoader />}>
        <ProductsImproved />
      </Suspense>
    ),
  },

  // Rotas protegidas
  {
    path: '/',
    element: <ProtectedLayout />,
    children: [
      {
        index: true,
        element: <Navigate to="/dashboard" replace />,
      },
      {
        path: 'dashboard',
        element: <Dashboard />,
      },
      {
        path: 'products',
        children: [
          {
            index: true,
            element: <ProductsImproved />,
          },
          {
            path: 'new',
            element: <ProductCreate />,
          },
          {
            path: ':id',
            element: <ProductDetail />,
          },
          {
            path: ':id/edit',
            element: <ProductEdit />,
          },
        ],
      },
      {
        path: 'orders',
        children: [
          {
            index: true,
            element: <Orders />,
          },
          {
            path: ':id',
            element: <OrderDetail />,
          },
        ],
      },
      {
        path: 'sync',
        children: [
          {
            index: true,
            element: <Sync />,
          },
          {
            path: 'dashboard',
            element: <SyncDashboardPage />,
          },
          {
            path: 'discovery',
            element: <DiscoveryPage />,
          },
          {
            path: 'jobs',
            element: <JobsPage />,
          },
          {
            path: 'product-history',
            element: <ProductHistory />,
          },
          {
            path: 'import-products',
            element: <ImportProductsUniversal />,
          },
          {
            path: 'import-categories',
            element: <ImportCategories />,
          },
          {
            path: 'import-attributes',
            element: <ImportAttributes />,
          },
          {
            path: 'import-customers',
            element: <ImportCustomers />,
          },
          {
            path: 'import-brands',
            element: <ImportBrands />,
          },
          {
            path: 'export-products',
            element: <ExportProducts />,
          },
          {
            path: 'export-customers',
            element: <ExportCustomers />,
          },
          {
            path: 'export-categories',
            element: <ExportCategories />,
          },
          {
            path: 'export-attributes',
            element: <ExportAttributes />,
          },
        ],
      },
      {
        path: 'sync-dashboard',
        element: <SyncDashboard />,
      },
      {
        path: 'settings',
        element: <Settings />,
      },
      {
        path: 'profile',
        element: <Profile />,
      },
      {
        path: 'components-demo',
        element: <ComponentsDemo />,
      },
      {
        path: 'configuration',
        children: [
          {
            path: 'platforms',
            element: <Platforms />,
          },
          {
            path: 'platforms/tiny',
            element: <TinyApiConfig />,
          },
          {
            path: 'platforms/bling',
            element: <BlingApiConfig />,
          },
          {
            path: 'platforms/:slug/api',
            element: <PlatformApiConfig />,
          },
          {
            path: 'platforms/linxmicrovix/:configId/de-para',
            element: <LinxDePara />,
          },
        ],
      },
      {
        path: 'brands',
        element: <Brands />,
      },
      {
        path: 'companies',
        element: <CompaniesRoute />,
      },
      {
        path: 'tasks',
        element: <Tasks />,
      },
      {
        path: 'users',
        element: <Users />,
      },
    ],
  },
  
  // Rota 404
  {
    path: '*',
    element: (
      <Suspense fallback={<PageLoader />}>
        <NotFound />
      </Suspense>
    ),
  },
]);

// Hook para usar as rotas
export const AppRouter: React.FC = () => {
  return <RouterProvider router={router} />;
};

export default AppRouter;
